/*
  Parachew cross-flavor sampler.

  RULE (rev 3, 2026-07-27): the gift is JAR-DEPENDENT.
    Strawberry Lemonade jar in cart -> 1x Caramel Coffee sachet
    Caramel Coffee jar in cart      -> 1x Strawberry Lemonade sachet
    Both jars in cart               -> NO sachet (rev 4, 2026-10-05: they already
                                       get a full jar of the other flavor)
  Quantity of jars never matters. Each earned sachet is capped at exactly 1.
  Mirrors earnedSamples() in parachew-website src/lib/cart.ts, change both together.

  rev 2 fixed the "sachet only shows up after you touch the cart again" bug:
  the theme renders the cart drawer straight from the /cart/add.js response,
  so a sachet added AFTER that call stayed invisible until the next repaint.
  The sachet is now injected into the SAME add request. Cart edits that swap
  or drop a sachet force a repaint via the Section Rendering API.
*/
(function () {
  var JAR_STRAWBERRY = 50393150357808; // Parachew, Strawberry Lemonade
  var JAR_CARAMEL    = 50393150390576; // Parachew, Caramel Coffee

  var SACHET_CARAMEL    = 67407751282992; // Parachew Sachet, Caramel Coffee
  var SACHET_STRAWBERRY = 67407751315760; // Parachew Sachet, Strawberry Lemonade

  // Every rule whose trigger is in the cart earns its gift. Not first-match.
  var RULES = [
    { trigger: JAR_STRAWBERRY, gift: SACHET_CARAMEL    }, // bought Strawberry, sample Caramel
    { trigger: JAR_CARAMEL,    gift: SACHET_STRAWBERRY }  // bought Caramel, sample Strawberry
  ];

  var GIFT_IDS  = RULES.map(function (r) { return r.gift; });
  var MUTATE_RE = /\/cart\/(add|change|update|clear)(\.js)?/;
  var ADD_RE    = /\/cart\/add(\.js)?/;
  var SECTIONS  = ['cart-drawer', 'cart-icon-bubble'];
  var SACHET_HANDLE = 'parachew-sachet';

  var nativeFetch = window.fetch.bind(window);
  var busy = false;

  function jget(url) {
    return nativeFetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function jpost(url, body) {
    return nativeFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function isGift(item) { return GIFT_IDS.indexOf(item.variant_id) !== -1; }

  function nonGiftIds(items) {
    return items.filter(function (i) { return !isGift(i); })
                .map(function (i) { return i.variant_id; });
  }

  // Returns the sachet the cart has earned. A cart holding BOTH jars earns none.
  function wantedSachets(ids) {
    var out = [];
    var jars = RULES.filter(function (r) { return ids.indexOf(r.trigger) !== -1; });
    if (jars.length > 1) return out;
    RULES.forEach(function (r) {
      if (ids.indexOf(r.trigger) !== -1 && out.indexOf(r.gift) === -1) out.push(r.gift);
    });
    return out;
  }

  function giftLine(want, id) {
    return { id: id, quantity: 1, properties: { _sampler_gift: 'true' } };
  }

  // Repaint the drawer + bubble from fresh cart data, the way the theme does.
  function forceRender() {
    return jpost('/cart/update.js', { updates: {}, sections: SECTIONS })
      .then(function (data) {
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents(data);
          var el = document.querySelector('cart-drawer.drawer');
          if (el && data.item_count > 0) el.classList.remove('is-empty');
        }
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        scheduleLock();
        // The dedicated /cart page has no drawer, so reload to repaint it.
        if (!drawer && location.pathname.indexOf('/cart') === 0) location.reload();
      })
      .catch(function () {});
  }

  // Clean-up pass: drop unearned sachets, clamp earned ones to 1, add missing ones.
  function reconcile() {
    if (busy) return Promise.resolve(false);
    busy = true;
    return jget('/cart.js').then(function (cart) {
      var want  = wantedSachets(nonGiftIds(cart.items));
      var gifts = cart.items.filter(isGift);

      var fixes = {}, changed = false;
      gifts.forEach(function (line) {
        if (want.indexOf(line.variant_id) === -1) { fixes[line.key] = 0; changed = true; }
        else if (line.quantity !== 1) { fixes[line.key] = 1; changed = true; }
      });

      var present = gifts.filter(function (l) { return want.indexOf(l.variant_id) !== -1; })
                         .map(function (l) { return l.variant_id; });
      var missing = want.filter(function (id) { return present.indexOf(id) === -1; });

      var step = changed ? jpost('/cart/update.js', { updates: fixes }) : Promise.resolve();
      return step.then(function () {
        if (missing.length) {
          return jpost('/cart/add.js', { items: missing.map(function (id) { return giftLine(want, id); }) })
            .then(function () { return true; });
        }
        return changed;
      });
    }).catch(function (e) {
      console.warn('[parachew-sampler]', e);
      return false;
    }).then(function (changed) {
      busy = false;
      return changed;
    });
  }

  /* ------------------------------------------------------------------
     Quantity lock. Every sachet is always exactly 1, so sachet lines must
     not expose a quantity control. Sachet lines ONLY, jars are untouched.
     reconcile() already clamps server-side; this stops a customer typing
     5 and watching it snap back, which reads as broken.
  ------------------------------------------------------------------ */
  // Verified against Shrine Pro 2026-07-27. Each line is an <li> carrying a
  // per-product class, e.g. "cart-item cart-item--product-parachew-sachet".
  // Do NOT use [class*="cart-item"]: that matches inner wrappers such as
  // .cart-item__quantity-wrapper, so closest() stops short of the real row
  // and the remove button never gets hidden.
  var ROW_SEL  = 'li[id^="CartDrawer-Item"], tr[id^="CartItem"], ' +
                 'li.cart-drawer-item, li.cart-item, tr.cart-item';
  var QTY_SEL  = 'input.quantity__input, input[name^="updates"], input[type="number"], quantity-input input';
  var KILL_SEL = 'button[name="minus"], button[name="plus"], .quantity__button, ' +
                 'cart-remove-button, .cart-item__remove, [id^="Remove-"], ' +
                 '[id^="CartDrawer-Remove"], a[href*="/cart/change"]';

  function lockRow(row) {
    if (!row || row.getAttribute('data-sampler-locked') === '1') return;
    row.setAttribute('data-sampler-locked', '1');
    Array.prototype.forEach.call(row.querySelectorAll(QTY_SEL), function (inp) {
      inp.readOnly = true;
      inp.setAttribute('aria-readonly', 'true');
      inp.setAttribute('tabindex', '-1');
      inp.style.pointerEvents = 'none';
      inp.style.opacity = '0.55';
      inp.title = 'One free sachet per order';
    });
    Array.prototype.forEach.call(row.querySelectorAll(KILL_SEL), function (el) {
      el.style.display = 'none';
    });
  }

  function lockSachetLines() {
    // Primary: the theme's own per-product line class. Both sachet variants
    // share the handle, so this covers either flavor. ".cart-item--product-parachew"
    // is a separate class token and never matches the sachet, so jars stay untouched.
    Array.prototype.forEach.call(
      document.querySelectorAll('.cart-item--product-' + SACHET_HANDLE), lockRow);

    // Fallback: walk up from the quantity input, keyed on the sachet variant ids.
    GIFT_IDS.forEach(function (id) {
      Array.prototype.forEach.call(
        document.querySelectorAll('[data-quantity-variant-id="' + id + '"]'),
        function (el) { lockRow(el.closest(ROW_SEL)); });
    });
  }

  var lockTimer = null;
  function scheduleLock() {
    clearTimeout(lockTimer);
    lockTimer = setTimeout(lockSachetLines, 120);
  }

  /* ------------------------------------------------------------------
     Fetch interception. This theme is 100% fetch-based (verified across
     all 446 theme files, zero XMLHttpRequest).
  ------------------------------------------------------------------ */
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';

    // Adding a jar: inject the earned sachet(s) into the SAME request so the
    // theme's own rendered response already contains them.
    if (ADD_RE.test(url) && init && typeof init.body === 'string' && !busy) {
      return (function () {
        var body;
        try { body = JSON.parse(init.body); } catch (e) { body = null; }
        if (!body || !body.items) return nativeFetch(input, init);

        var incoming = body.items.map(function (i) { return Number(i.id); });
        if (!incoming.some(function (id) {
              return id === JAR_STRAWBERRY || id === JAR_CARAMEL; })) {
          return nativeFetch(input, init);
        }

        return jget('/cart.js').then(function (cart) {
          var want    = wantedSachets(nonGiftIds(cart.items).concat(incoming));
          var present = cart.items.filter(isGift).map(function (i) { return i.variant_id; });
          var missing = want.filter(function (id) { return present.indexOf(id) === -1; });
          if (missing.length) {
            body.items = body.items.concat(missing.map(function (id) { return giftLine(want, id); }));
            init = Object.assign({}, init, { body: JSON.stringify(body) });
          }
          return nativeFetch(input, init);
        }).then(function (res) {
          reconcile().then(function (changed) { if (changed) forceRender(); else scheduleLock(); });
          return res;
        }).catch(function () { return nativeFetch(input, init); });
      })();
    }

    var call = nativeFetch(input, init);
    if (MUTATE_RE.test(url) && !busy) {
      call.then(function () {
        reconcile().then(function (changed) { if (changed) forceRender(); else scheduleLock(); });
      });
    }
    return call;
  };

  document.addEventListener('DOMContentLoaded', function () {
    reconcile().then(function (changed) { if (changed) forceRender(); else scheduleLock(); });
    new MutationObserver(scheduleLock).observe(document.body, { childList: true, subtree: true });
  });
})();

